<template>
  <div class="md:w-368 mx-auto">
    <JobPostScheduleCard
      id="schedule-type-occasional"
      title="Occasional sitter"
      body="As-needed for when you need care"
      :shadow="false"
      class="mb-16"
    >
      <OccasionalScheduleIcon />
    </JobPostScheduleCard>
    <Card class="w-full md:p-24">
      <form>
        <div class="mb-32">
          <poppins-date-picker
            id="occasional-schedule-datepicker"
            v-model="startDate"
            labelName="Start date"
            :minDate="minStartDate"
            :maxDate="maxStartDate"
            :status="helperStatus"
            :helperText="errors.startDate"
            class="mb-16"
            @input="$delete(errors, 'startDate')"
          />
          <Checkbox v-model="startDateFlexible" labelName="Start date is flexible" checkboxId="start-date-flexible" />
        </div>
        <div class="flex flex-row-reverse">
          <RouteStepperNextButton
            :stepData="reviewSection"
            :draftData="scheduleData"
            :validate="validate"
            @errors="errors = $event"
          />
        </div>
      </form>
    </Card>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import PoppinsDatePicker from '@/components/PoppinsDatePicker/PoppinsDatePicker.vue';
import Checkbox from '@/components/checkbox/Checkbox.vue';
import OccasionalScheduleIcon from '@/icons/OccasionalScheduleIcon.vue';
import JobPostScheduleCard from './JobPostScheduleCard.vue';
import { minStartDate, maxStartDate, status, helperText } from './schedule_helpers';
import Card from '@/components/card/Card.vue';
import { ReviewSection } from '@/components/route_stepper/types';
import JobPostScheduleOccasionalReview from './JobPostScheduleOccasionalReview.vue';
import { OccasionalJobSchedule } from '@/store/modules/job/types';
import { JobPostDraft } from '../types';
import { HelperStatus } from '@/helpers/status';
import RouteStepperNextButton from '@/components/route_stepper/RouteStepperNextButton.vue';
import { formatISO, format } from 'date-fns';

interface Errors {
  startDate?: string;
}

interface JobPostScheduleOccasionalData {
  startDateFlexible: boolean;
  startDate: Date | null;
  errors: Errors;
}

export default {
  name: 'JobPostScheduleOccasional',
  components: {
    PoppinsDatePicker,
    OccasionalScheduleIcon,
    JobPostScheduleCard,
    Checkbox,
    Card,
    RouteStepperNextButton,
  },
  props: {
    draft: {
      type: Object as PropType<JobPostDraft>,
      required: true,
    },
  },
  data(): JobPostScheduleOccasionalData {
    return {
      startDateFlexible: this.draft?.schedule?.occasionalJobSchedule?.isStartDateFlexible || false,
      startDate: this.draft?.schedule?.occasionalJobSchedule?.startDate
        ? new Date(this.draft.schedule.occasionalJobSchedule.startDate)
        : null,
      errors: {},
    };
  },
  computed: {
    minStartDate,
    maxStartDate,
    helperStatus(): HelperStatus {
      return this.errors.startDate ? HelperStatus.Error : HelperStatus.Neutral;
    },
    reviewSection(): ReviewSection | undefined {
      if (!this.startDate) {
        return undefined;
      }

      return {
        title: 'Schedule',
        component: JobPostScheduleOccasionalReview,
        props: {
          formattedStartDate: format(this.startDate, 'eeee, MMMM d, yyyy'),
          startDateFlexible: this.startDateFlexible,
        },
        editPath: this.$route.path,
      };
    },
    occasionalJobSchedule(): OccasionalJobSchedule | undefined {
      if (!this.startDate) {
        return undefined;
      }

      return {
        startDate: formatISO(this.startDate, { representation: 'date' }),
        isStartDateFlexible: this.startDateFlexible,
      };
    },
    scheduleData(): JobPostDraft {
      return {
        schedule: {
          occasionalJobSchedule: this.occasionalJobSchedule,
        },
      };
    },
  },
  methods: {
    status,
    helperText,
    validate(): Errors {
      const errors: Errors = {};
      if (!this.startDate) {
        errors.startDate = 'Please select a start date.';
      }
      return errors;
    },
  },
};
</script>
